import React from 'react'

import numeral from 'numeral'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import type { LineSeriesType, HighlightItemData } from '@mui/x-charts'
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip'
import { LineChart } from '@mui/x-charts/LineChart'

import { DATE_FORMATS_INTL, formatDate } from '../../../core/constants'
import { useScreenSize } from '../../hooks/useScreenSize'
import { useDimensions } from '../../hooks/useDimensions'
import CustomAreaTooltip from './stackedlinechart/CustomAreaTooltip'
import { CustomLegend } from './stackedlinechart/CustomLegend'
import { CustomPointTooltip } from './stackedlinechart/CustomPointTooltip'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

type DataPoint = { x: Date; y: number }
type Dataset = DataPoint[]

interface StackedLineChartData {
  top_level_category_guid: string
  category_guid: string
  category_name: string
  category_color: string
  dataset: Dataset
}

interface StackedLineChartProps {
  stackedDatasets: StackedLineChartData[]
  unstackedDatasets?: StackedLineChartData[]
  onHoverArea?: (category: string) => void
  onHoverPoint?: (category: string) => void
  onHoverLegend?: (category: string) => void
  onClickLegend?: (category: string) => void
  availableHeight?: number
  minimumHeight?: number
}

// TODO: Use v8 X-Charts built-in features to avoid custom implementation

export const StackedLineChart: React.FC<StackedLineChartProps> = ({
  onHoverArea,
  onHoverPoint,
  onHoverLegend,
  onClickLegend,
  stackedDatasets,
  unstackedDatasets = [],
  availableHeight = 0,
  minimumHeight = 350,
}) => {
  const theme = useTheme()
  const { isMobile } = useScreenSize()
  const [legendRef, { height: legendHeight }] = useDimensions()
  const chartContainerRef = React.useRef<HTMLDivElement>(null)
  const [chartHeight, setChartHeight] = React.useState<number>(0)

  // Control responsive chart height based on available height
  React.useEffect(() => {
    const newChartHeight = Math.max(availableHeight - legendHeight, minimumHeight)
    setChartHeight(newChartHeight)
  }, [availableHeight, legendHeight])

  const [hoveredLegend, setHoveredLegend] = React.useState<string | null>(null)
  const [hoverState, setHoverState] = React.useState({
    hoveredSeriesId: null as string | null,
    hoveredAreaData: null as DataPoint[] | null,
    isAreaHovered: false,
    cursorPos: { x: 0, y: 0 },
  })

  const { hoveredSeriesId, hoveredAreaData, isAreaHovered, cursorPos } = hoverState
  const showAreaTooltip = isAreaHovered && hoveredSeriesId && hoveredAreaData
  const showPointTooltip = !showAreaTooltip

  const datasets = [...stackedDatasets, ...unstackedDatasets]
  const xTicks = datasets[0]?.dataset.length ?? 0
  const xData = datasets.flatMap((data) => data.dataset.map((d) => d.x))
  const yData = datasets.flatMap((data) => data.dataset.map((d) => d.y))

  // Calculate max cumulative heights for stacked datasets
  const cumulativeHeights = stackedDatasets[0]?.dataset?.map((_, i) =>
    stackedDatasets.reduce((sum, dataset) => sum + dataset.dataset[i]?.y || 0, 0),
  ) ?? [0]
  const stackedMax = Math.max(...cumulativeHeights)
  const unstackedMax = Math.max(
    ...unstackedDatasets.flatMap((data) => data.dataset.map((d) => d.y)),
  )

  const yMin = Math.floor(Math.min(...yData) / 100) * 100
  const yMax = Math.ceil(Math.max(stackedMax, unstackedMax) / 100) * 100

  const stackedSeries = stackedDatasets.map((data, index) => ({
    id: `stacked-${index}`,
    guid: data.category_guid,
    top_level_guid: data.top_level_category_guid,
    area: true,
    color: stackedDatasets[index].category_color ?? theme.palette.primary.main,
    data: data.dataset.map((d) => d.y),
    label: stackedDatasets[index].category_name,
    stack: 'total',
    type: 'line',
    highlightScope: { highlight: 'item' },
    valueFormatter: (value: number) => formatCurrency(value, '0,0'),
  }))

  const unstackedSeries = unstackedDatasets.map((data, index) => ({
    id: `unstacked-${index}`,
    guid: data.category_guid,
    top_level_guid: data.top_level_category_guid,
    area: false,
    color: data.category_color,
    data: data.dataset.map((d) => d.y),
    label: data.category_name,
    type: 'line',
    valueFormatter: (value: number) => formatCurrency(value, '0,0'),
  }))

  const series = [...stackedSeries, ...unstackedSeries]
  const guids = [
    ...stackedSeries.map(({ guid, top_level_guid }) => ({
      guid,
      top_level_guid,
    })),
    ...unstackedSeries.map(({ guid, top_level_guid }) => ({
      guid,
      top_level_guid,
    })),
  ]

  const getSeriesCategoryGuids = (seriesId: string) => {
    const isStacked = seriesId.startsWith('stacked-')
    const dataset = isStacked
      ? stackedDatasets[parseInt(seriesId.split('-')[1])]
      : unstackedDatasets[parseInt(seriesId.split('-')[1])]
    return {
      guid: dataset?.category_guid,
      topLevelGuid: dataset?.top_level_category_guid,
    }
  }

  const handleHoverLegend = (guid: string | null) => {
    setHoveredLegend(guid)
    onHoverLegend?.(guid ?? '')
  }

  const handleClickLegend = (guid: string | null) => {
    onClickLegend?.(guid ?? '')
  }

  const handleClickArea = () => {
    const clickedSeriesGuid = getSeriesCategoryGuids(String(hoveredSeriesId)).guid
    if (clickedSeriesGuid) onClickLegend?.(clickedSeriesGuid)
  }

  const handleHighlightChange = (highlightedItem: HighlightItemData | null) => {
    const hoveredSeriesId = String(highlightedItem?.seriesId)
    const hoveredSeriesGuid = getSeriesCategoryGuids(hoveredSeriesId).guid
    const hoveredSeries = series.find((s) => s.id === hoveredSeriesId)
    const isPointHovered = hoveredSeries && highlightedItem?.dataIndex !== undefined
    const isAreaHovered = hoveredSeries && highlightedItem?.dataIndex === undefined

    if (isPointHovered) {
      setHoverState({
        hoveredSeriesId: hoveredSeriesId,
        hoveredAreaData: null,
        isAreaHovered: false,
        cursorPos,
      })
      onHoverPoint?.(hoveredSeriesGuid)
    } else if (isAreaHovered) {
      const hoveredDataPoints =
        hoveredSeries?.data.map((yValue, index) => ({
          x: xData[index],
          y: yValue,
        })) ?? []

      setHoverState({
        hoveredSeriesId: hoveredSeriesId,
        hoveredAreaData: hoveredDataPoints,
        isAreaHovered: true,
        cursorPos,
      })
      onHoverArea?.(hoveredSeriesGuid)
    } else {
      setHoverState({
        hoveredSeriesId: null,
        hoveredAreaData: null,
        isAreaHovered: false,
        cursorPos,
      })
    }
  }

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.target instanceof Element &&
      event.target.classList[0]?.includes('MuiMarkElement-root')
    )
      return

    const rect = event.currentTarget.getBoundingClientRect()
    setHoverState((prev) => ({
      ...prev,
      cursorPos: {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      },
    }))
  }

  const customStyles = {
    p: 4,
    '& .MuiBarLabel-root': {
      fontSize: 10,
      fontWeight: 600,
    },
    '& .MuiAreaElement-root': {
      opacity: 0.4,
      transition: 'opacity 0.3s ease',
    },
    '& .MuiAreaElement-root:hover': {
      opacity: 1,
    },
    '& [class*="MuiLineElement-series-unstacked-"]': {
      strokeWidth: 3,
    },
    ...(hoveredLegend &&
      stackedSeries.reduce<Record<string, any>>((acc, _, index) => {
        const selector = `.MuiAreaElement-series-stacked-${index}`
        acc[selector] = {
          opacity: stackedSeries[index].guid === hoveredLegend ? 0.9 : 0.2,
        }
        return acc
      }, {})),
  }

  return (
    <Box
      onMouseMove={handleMouseMove}
      ref={chartContainerRef}
      sx={{
        position: 'relative',
        touchAction: 'pan-y',
        '& svg': {
          touchAction: 'pan-y',
          userSelect: 'none',
        },
        '& .recharts-wrapper': {
          touchAction: 'pan-y',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'column' }}
        spacing={{ xs: 0, md: 4 }}
        sx={{ width: '100%', position: 'relative' }}
      >
        <LineChart
          height={chartHeight}
          hideLegend={true}
          margin={{ bottom: 24, left: 12, right: 54, top: 48 }}
          onAreaClick={handleClickArea}
          onHighlightChange={handleHighlightChange}
          series={series as LineSeriesType[]}
          skipAnimation={true}
          slotProps={{
            tooltip: { trigger: showPointTooltip ? 'item' : 'none' },
          }}
          slots={{
            tooltip:
              showPointTooltip && hoveredSeriesId
                ? (props) => (
                    <ChartsTooltipContainer>
                      <CustomPointTooltip
                        {...props}
                        categoryGuids={getSeriesCategoryGuids(hoveredSeriesId)}
                        series={series}
                        xData={xData}
                      />
                    </ChartsTooltipContainer>
                  )
                : undefined,
          }}
          sx={customStyles}
          xAxis={[
            {
              data: xData,
              scaleType: 'time',
              valueFormatter: (value: Date) =>
                formatDate(value, DATE_FORMATS_INTL.MONTH_SHORT_YEAR),
              tickNumber: xTicks, // How many ticks to show on the x-axis
            },
          ]}
          yAxis={[
            {
              min: yMin,
              max: yMax || 100,
              valueFormatter: (value: number) => numeral(value).format('0a'),
            },
          ]}
        />
        <CustomLegend
          categoryGuids={guids}
          hoveredLegend={hoveredLegend}
          legendRef={legendRef}
          onClickLegend={handleClickLegend}
          onHoverLegend={handleHoverLegend}
          sx={{ px: isMobile ? 0 : 24 }}
        />

        {showAreaTooltip && (
          <CustomAreaTooltip
            categoryGuids={getSeriesCategoryGuids(hoveredSeriesId)}
            chartContainerRef={chartContainerRef}
            cursorPosition={cursorPos}
            hoveredAreaData={hoveredAreaData.map((point) => ({
              ...point,
              x: formatDate(point.x, DATE_FORMATS_INTL.MONTH_SHORT_YEAR),
            }))}
          />
        )}
      </Stack>
    </Box>
  )
}
